import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  AcademicCapIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  TrophyIcon,
  ArrowRightIcon,
  StarIcon,
  ExclamationTriangleIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';

const SkillAssessment = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('available');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showStartModal, setShowStartModal] = useState(false);

  // Mock data - in real app this would come from API
  const availableAssessments = [
    {
      id: 1,
      title: 'Healthcare Management Fundamentals',
      description: 'Assess your knowledge of basic healthcare management principles, leadership, and organizational behavior.',
      category: 'Management',
      difficulty: 'Intermediate',
      duration: 30,
      questions: 25,
      passingScore: 80,
      attempts: 3,
      usedAttempts: 0,
      skills: ['Healthcare Leadership', 'Strategic Planning', 'Team Management', 'Quality Improvement'],
      prerequisite: 'Healthcare Management course completion recommended',
      certification: true,
      points: 150
    },
    {
      id: 2,
      title: 'Medical Ethics Assessment',
      description: 'Evaluate your understanding of medical ethics, patient rights, informed consent, and moral decision-making.',
      category: 'Ethics',
      difficulty: 'Advanced',
      duration: 45,
      questions: 30,
      passingScore: 85,
      attempts: 2,
      usedAttempts: 0,
      skills: ['Medical Ethics', 'Patient Rights', 'Informed Consent', 'Moral Reasoning'],
      prerequisite: 'Medical Ethics and Patient Care course completion',
      certification: true,
      points: 200
    },
    {
      id: 3,
      title: 'Emergency Response Skills',
      description: 'Test your knowledge of emergency protocols, crisis management, and rapid response procedures.',
      category: 'Emergency',
      difficulty: 'Advanced',
      duration: 40,
      questions: 35,
      passingScore: 90,
      attempts: 2,
      usedAttempts: 1,
      skills: ['Emergency Protocols', 'Crisis Management', 'Rapid Assessment', 'Team Coordination'],
      prerequisite: 'Emergency Response Protocols course completion',
      certification: true,
      points: 250
    },
    {
      id: 4,
      title: 'Digital Health Technology',
      description: 'Assess your understanding of digital health innovations, telemedicine, and technology implementation.',
      category: 'Technology',
      difficulty: 'Beginner',
      duration: 25,
      questions: 20,
      passingScore: 75,
      attempts: 3,
      usedAttempts: 0,
      skills: ['Digital Health', 'Telemedicine', 'Health Apps', 'Technology Implementation'],
      prerequisite: 'None',
      certification: false,
      points: 100
    }
  ];

  const completedAssessments = [
    {
      id: 5,
      title: 'Patient Communication Skills',
      category: 'Communication',
      completedDate: '2025-01-15',
      score: 92,
      passingScore: 80,
      duration: 20,
      questions: 15,
      timeSpent: 18,
      status: 'passed',
      certificateId: 'ASSESS-001',
      skills: ['Patient Communication', 'Active Listening', 'Empathy Training'],
      feedback: 'Excellent understanding of patient communication principles. Strong performance in empathy and active listening scenarios.'
    },
    {
      id: 6,
      title: 'Healthcare Data Analytics',
      category: 'Analytics',
      completedDate: '2025-01-08',
      score: 88,
      passingScore: 85,
      duration: 35,
      questions: 25,
      timeSpent: 32,
      status: 'passed',
      certificateId: 'ASSESS-002',
      skills: ['Data Analysis', 'Predictive Modeling', 'Healthcare Metrics'],
      feedback: 'Good grasp of analytics concepts. Consider additional practice with predictive modeling techniques.'
    },
    {
      id: 7,
      title: 'Clinical Decision Making',
      category: 'Clinical',
      completedDate: '2024-12-20',
      score: 72,
      passingScore: 80,
      duration: 40,
      questions: 30,
      timeSpent: 45,
      status: 'failed',
      certificateId: null,
      skills: ['Clinical Assessment', 'Diagnostic Reasoning', 'Treatment Planning'],
      feedback: 'Needs improvement in diagnostic reasoning and evidence-based decision making. Recommend reviewing clinical assessment methodologies.'
    }
  ];

  const skillsOverview = [
    {
      skill: 'Healthcare Management',
      level: 'Advanced',
      progress: 85,
      assessments: 3,
      lastUpdated: '2025-01-15'
    },
    {
      skill: 'Patient Communication',
      level: 'Expert',
      progress: 92,
      assessments: 2,
      lastUpdated: '2025-01-15'
    },
    {
      skill: 'Medical Ethics',
      level: 'Intermediate',
      progress: 70,
      assessments: 1,
      lastUpdated: '2025-01-08'
    },
    {
      skill: 'Data Analytics',
      level: 'Intermediate',
      progress: 75,
      assessments: 1,
      lastUpdated: '2025-01-08'
    },
    {
      skill: 'Clinical Skills',
      level: 'Beginner',
      progress: 45,
      assessments: 1,
      lastUpdated: '2024-12-20'
    }
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setIsLoading(false), 800);
  }, []);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSkillLevelColor = (level) => {
    switch (level) {
      case 'Expert': return 'bg-purple-100 text-purple-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Beginner': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStartAssessment = (assessment) => {
    setSelectedAssessment(assessment);
    setShowStartModal(true);
  };

  const confirmStartAssessment = () => {
    setShowStartModal(false);
    // In real app, this would navigate to the assessment interface
    navigate(`/training/assessment/${selectedAssessment.id}/start`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-accent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ChartBarIcon className="h-8 w-8 text-apollo-accent mr-3" />
                Skill Assessments
              </h1>
              <p className="mt-2 text-gray-600">
                Test your knowledge and earn skill certifications
              </p>
            </div>
            
            <button
              onClick={() => navigate('/training/my-training')}
              className="bg-apollo-accent text-white px-6 py-3 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200 flex items-center"
            >
              <AcademicCapIcon className="h-5 w-5 mr-2" />
              My Training
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-8">
          <nav className="flex space-x-8 px-6">
            {['available', 'completed', 'skills'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab
                    ? 'border-apollo-accent text-apollo-accent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'available' && 'Available Assessments'}
                {tab === 'completed' && 'Completed'}
                {tab === 'skills' && 'Skills Overview'}
              </button>
            ))}
          </nav>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Available Assessments Tab */}
            {activeTab === 'available' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Available Assessments</h2>
                  <span className="text-sm text-gray-600">
                    {availableAssessments.length} assessments available
                  </span>
                </div>

                {availableAssessments.map((assessment) => (
                  <div key={assessment.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {assessment.title}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(assessment.difficulty)}`}>
                            {assessment.difficulty}
                          </span>
                          {assessment.certification && (
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                              Certificate
                            </span>
                          )}
                        </div>
                        
                        <p className="text-gray-600 mb-3">
                          {assessment.description}
                        </p>
                        
                        <div className="flex items-center text-sm text-gray-600 space-x-4 mb-3">
                          <span className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {assessment.duration} minutes
                          </span>
                          <span className="flex items-center">
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            {assessment.questions} questions
                          </span>
                          <span className="flex items-center">
                            <TrophyIcon className="h-4 w-4 mr-1" />
                            {assessment.points} points
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {assessment.skills.map((skill, index) => (
                            <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                              {skill}
                            </span>
                          ))}
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          <p><span className="font-medium">Passing Score:</span> {assessment.passingScore}%</p>
                          <p><span className="font-medium">Attempts:</span> {assessment.usedAttempts}/{assessment.attempts}</p>
                          {assessment.prerequisite !== 'None' && (
                            <p><span className="font-medium">Prerequisite:</span> {assessment.prerequisite}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="ml-6 text-center">
                        <div className="text-sm text-gray-600 mb-2">
                          Attempts Left
                        </div>
                        <div className="text-2xl font-bold text-apollo-accent mb-4">
                          {assessment.attempts - assessment.usedAttempts}
                        </div>
                        
                        <button
                          onClick={() => handleStartAssessment(assessment)}
                          disabled={assessment.usedAttempts >= assessment.attempts}
                          className={`px-4 py-2 rounded-lg transition-colors duration-200 flex items-center ${
                            assessment.usedAttempts >= assessment.attempts
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-apollo-accent text-white hover:bg-apollo-accent/90'
                          }`}
                        >
                          <PlayIcon className="h-4 w-4 mr-1" />
                          Start Assessment
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Completed Assessments Tab */}
            {activeTab === 'completed' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Completed Assessments</h2>
                  <span className="text-sm text-gray-600">
                    {completedAssessments.length} assessments completed
                  </span>
                </div>

                {completedAssessments.map((assessment) => (
                  <div key={assessment.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start space-x-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          assessment.status === 'passed' 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {assessment.status === 'passed' ? (
                            <CheckCircleIcon className="h-6 w-6" />
                          ) : (
                            <XCircleIcon className="h-6 w-6" />
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {assessment.title}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              assessment.status === 'passed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {assessment.status === 'passed' ? 'Passed' : 'Failed'}
                            </span>
                          </div>
                          
                          <div className="flex items-center text-sm text-gray-600 space-x-4 mb-3">
                            <span>Completed: {new Date(assessment.completedDate).toLocaleDateString()}</span>
                            <span>Time: {assessment.timeSpent}/{assessment.duration} min</span>
                            <span>Questions: {assessment.questions}</span>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 mb-3">
                            {assessment.skills.map((skill, index) => (
                              <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">Feedback:</span> {assessment.feedback}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className={`text-3xl font-bold mb-1 ${
                          assessment.score >= assessment.passingScore ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {assessment.score}%
                        </div>
                        <div className="text-sm text-gray-600 mb-4">
                          Required: {assessment.passingScore}%
                        </div>
                        
                        {assessment.certificateId && (
                          <button
                            onClick={() => navigate(`/training/certificates/${assessment.certificateId}`)}
                            className="text-apollo-accent hover:text-apollo-accent/80 text-sm font-medium flex items-center"
                          >
                            <TrophyIcon className="h-4 w-4 mr-1" />
                            View Certificate
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Skills Overview Tab */}
            {activeTab === 'skills' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Skills Overview</h2>
                  <span className="text-sm text-gray-600">
                    {skillsOverview.length} skills tracked
                  </span>
                </div>

                {skillsOverview.map((skill, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-apollo-accent to-blue-600 rounded-lg flex items-center justify-center">
                          <StarIcon className="h-6 w-6 text-white" />
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {skill.skill}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSkillLevelColor(skill.level)}`}>
                              {skill.level}
                            </span>
                          </div>
                          
                          <div className="flex items-center text-sm text-gray-600 space-x-3">
                            <span>{skill.assessments} assessments</span>
                            <span>Last updated: {new Date(skill.lastUpdated).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-2xl font-bold text-apollo-accent mb-1">
                          {skill.progress}%
                        </div>
                        <div className="text-sm text-gray-600">
                          Proficiency
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-apollo-accent h-3 rounded-full transition-all duration-300"
                          style={{ width: `${skill.progress}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Based on {skill.assessments} completed assessment{skill.assessments !== 1 ? 's' : ''}
                      </div>
                      
                      <button className="text-apollo-accent hover:text-apollo-accent/80 text-sm font-medium flex items-center">
                        View Details
                        <ArrowRightIcon className="h-4 w-4 ml-1" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Assessment Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className="h-5 w-5 text-apollo-accent mr-2" />
                Assessment Stats
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Completed</span>
                  <span className="font-semibold text-gray-900">{completedAssessments.length}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Passed</span>
                  <span className="font-semibold text-green-600">
                    {completedAssessments.filter(a => a.status === 'passed').length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Average Score</span>
                  <span className="font-semibold text-gray-900">
                    {Math.round(completedAssessments.reduce((acc, a) => acc + a.score, 0) / completedAssessments.length)}%
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Points</span>
                  <span className="font-semibold text-apollo-accent">
                    {completedAssessments.filter(a => a.status === 'passed').length * 150}
                  </span>
                </div>
              </div>
            </div>

            {/* Upcoming Recommendations */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BookOpenIcon className="h-5 w-5 text-apollo-accent mr-2" />
                Recommended Next
              </h3>
              
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-1">Healthcare Management</h4>
                  <p className="text-sm text-blue-700 mb-2">
                    Build on your completed courses with this comprehensive assessment.
                  </p>
                  <div className="flex items-center text-xs text-blue-600">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    30 minutes • 25 questions
                  </div>
                </div>
                
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-yellow-900 mb-1">Medical Ethics</h4>
                  <p className="text-sm text-yellow-700 mb-2">
                    Advanced assessment for ethics certification.
                  </p>
                  <div className="flex items-center text-xs text-yellow-600">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    45 minutes • 30 questions
                  </div>
                </div>
              </div>
            </div>

            {/* Study Tips */}
            <div className="bg-gradient-to-br from-apollo-accent to-blue-600 rounded-xl p-6 text-white">
              <h3 className="text-lg font-semibold mb-3">Assessment Tips 💡</h3>
              
              <ul className="space-y-2 text-sm opacity-90">
                <li>• Review course materials before attempting</li>
                <li>• Take practice assessments when available</li>
                <li>• Manage your time wisely during tests</li>
                <li>• Read questions carefully before answering</li>
                <li>• Use all available attempts strategically</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Start Assessment Modal */}
        {showStartModal && selectedAssessment && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-apollo-accent/10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-apollo-accent" />
                </div>
                
                <div className="mt-3 text-center">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Start Assessment
                  </h3>
                  
                  <div className="mt-4 text-left">
                    <p className="text-sm text-gray-600 mb-3">
                      You are about to start: <strong>{selectedAssessment.title}</strong>
                    </p>
                    
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Duration:</span>
                          <div className="font-medium">{selectedAssessment.duration} minutes</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Questions:</span>
                          <div className="font-medium">{selectedAssessment.questions}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Passing Score:</span>
                          <div className="font-medium">{selectedAssessment.passingScore}%</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Attempts Left:</span>
                          <div className="font-medium">{selectedAssessment.attempts - selectedAssessment.usedAttempts}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                        <div className="text-sm text-yellow-700">
                          <p className="font-medium mb-1">Important:</p>
                          <ul className="list-disc list-inside space-y-1">
                            <li>You cannot pause once started</li>
                            <li>All questions must be completed</li>
                            <li>Results will be available immediately</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-6">
                    <button
                      onClick={() => setShowStartModal(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md hover:bg-gray-400 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    
                    <button
                      onClick={confirmStartAssessment}
                      className="px-4 py-2 bg-apollo-accent text-white text-base font-medium rounded-md hover:bg-apollo-accent/90 transition-colors duration-200"
                    >
                      Start Assessment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default SkillAssessment;
